interface SessionData {
  sessionId: string;
  createdAt: number;
  lastActivity: number;
  scenarioId?: string;
  isFinished: boolean;
}

const SESSION_STORAGE_KEY = 'gesprek-ai-session';
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours
const ACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours of inactivity

export const createSessionId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getOrCreateSession = (scenarioId?: string): string => {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const sessionData: SessionData = JSON.parse(stored);
      const now = Date.now();
      
      // Check if session is expired or finished
      const isExpired = (now - sessionData.createdAt) > SESSION_TIMEOUT;
      const isInactive = (now - sessionData.lastActivity) > ACTIVITY_TIMEOUT;
      
      if (!isExpired && !isInactive && !sessionData.isFinished) {
        // Update last activity and return existing session
        sessionData.lastActivity = now;
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
        return sessionData.sessionId;
      }
    }
    
    // Create new session
    const newSessionId = createSessionId();
    const newSessionData: SessionData = {
      sessionId: newSessionId,
      createdAt: Date.now(),
      lastActivity: Date.now(),
      scenarioId,
      isFinished: false
    };
    
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSessionData));
    return newSessionId;
  } catch (error) {
    console.warn('Could not access localStorage, creating temporary session:', error);
    return createSessionId();
  }
};

export const updateSessionActivity = (): void => {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const sessionData: SessionData = JSON.parse(stored);
      sessionData.lastActivity = Date.now();
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    }
  } catch (error) {
    console.warn('Could not update session activity:', error);
  }
};

export const finishSession = (): void => {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const sessionData: SessionData = JSON.parse(stored);
      sessionData.isFinished = true;
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    }
  } catch (error) {
    console.warn('Could not finish session:', error);
  }
};

export const clearSession = (): void => {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.warn('Could not clear session:', error);
  }
};
